import { Ship } from '../interfaces/types';

/** Formato simple (fleetviewer.link / importación lista de naves). */
export interface FleetViewerShipEntry {
  name: string;
  shipname: string;
  type: 'ship';
}

export type FleetViewerExport = FleetViewerShipEntry[];

const fmt = (value: number): string => value.toFixed(10);

const createId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/** Slug para shipSlug de Starjump (URL RSI o nombre en kebab-case). */
export const toStarjumpShipSlug = (ship: Ship): string => {
  if (ship.url) {
    try {
      const path = new URL(ship.url, 'https://robertsspaceindustries.com').pathname;
      const segment = path.split('/').filter(Boolean).pop();
      if (segment) return segment.toLowerCase();
    } catch {
      // usar fallback por nombre
    }
  }
  return ship.name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/gi, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

const displayShipLabel = (ship: Ship): string => ship.nick?.trim() || ship.name;

const uniqueShipsByName = (ships: Ship[]): Ship[] => {
  const byName = new Map<string, Ship>();
  ships.forEach((ship) => {
    if (!ship.name) return;
    if (!byName.has(ship.name)) byName.set(ship.name, ship);
  });
  return Array.from(byName.values());
};

const getStarjumpShipVisuals = (ship: Ship): {
  imageRes: string;
  imageScale: string;
  width: string;
  height: string;
  textGroupY: string;
  textWidth: string;
} => {
  const size = (ship.size || 'small').toLowerCase();
  const lengthM = ship.length > 0 ? ship.length : 25;
  const beamM = ship.beam > 0 ? ship.beam : 15;

  let unitScale = 200;
  let imageRes = 's';
  let imageScale = 3.8;

  if (size === 'capital' || size === 'c') {
    unitScale = 1000;
    imageRes = 's';
    imageScale = 12.2;
  } else if (size === 'large' || size === 'l') {
    unitScale = 465;
    imageRes = 'l';
    imageScale = 4.49;
  } else if (size === 'medium' || size === 'm') {
    unitScale = 186;
    imageRes = 's';
    imageScale = 4.65;
  } else if (size === 'small' || size === 's') {
    unitScale = 152;
    imageRes = 's';
    imageScale = 2.6;
  } else {
    unitScale = 152;
    imageRes = 's';
    imageScale = 3.8;
  }

  const width = lengthM * unitScale;
  const height = beamM * unitScale * 1.32;
  const label = displayShipLabel(ship);

  return {
    imageRes,
    imageScale: fmt(imageScale),
    width: fmt(width),
    height: fmt(height),
    textGroupY: fmt(height + 144),
    textWidth: fmt(Math.max(1453, label.length * 72)),
  };
};

const STARJUMP_TEXTGROUP_DEFAULTS = {
  rotation: '0.0000000000',
  height: '1200.0000000000',
  align: 'left',
  fontRes: 'm',
  fontFamily: 'Font1',
  statfontRes: 's',
  statfontFamily: 'Font1',
  stats: ['LENGTH'] as string[],
  locked: 'false',
  selectionGroup: '',
  isometricMode: 'none',
  gradientType: 'none',
  gradientColor1: 'b3ffffff',
  gradientColor2: 'ff000000',
  gradientColor3: 'ff808080',
  gradientAngle: '90.0000000000',
  gradientCenterX: '0.5000000000',
  gradientCenterY: '0.5000000000',
  gradientRadius: '0.8000000000',
  sweepStartAngle: '0.0000000000',
  sweepStopAngle: '360.0000000000',
  sweepAngleOffset: '0.0000000000',
  dropShadow: 'false',
  elevation: '0.0000000000',
  glow: 'false',
  glowColor: 'ffffffff',
  glowRadius: '0.0000000000',
};

/** Plantilla global Starjump (valores por defecto del visor). */
const STARJUMP_DOCUMENT_DEFAULTS = {
  type: 'starjumpFleetviewer',
  version: 1,
  backgroundColor: '00151515',
  backgroundImageUrl: 'https://cdn3.fleetviewer.link/starjump-spacescape.png',
  backgroundImageBase64: '',
  backgroundRotation: '0.0000000000',
  canvasZoom: '0.0372759372',
  canvasPanX: '0.0000000000',
  canvasPanY: '0.0000000000',
  globalItemZoom: '0.0372759372',
  globalItemPanX: '0.0000000000',
  globalItemPanY: '0.0000000000',
  viewportZoom: '0.6619791667',
  viewportPanX: '-0.0000000000',
  viewportPanY: '-0.0000000000',
  viewportMinZoom: '0.0500000000',
  viewportMaxZoom: '8.0000000000',
  renderBoxX: '0.0000000000',
  renderBoxY: '0.0000000000',
  renderBoxWidth: '3840.0000000000',
  renderBoxHeight: '2160.0000000000',
  communityIconX: '1820.0000000000',
  communityIconY: '980.0000000000',
  starjumpIconX: '-1795.0000000000',
  starjumpIconY: '955.0000000000',
  shipScaleFactor: '4.0000000000',
  minZoom: '0.0010000000',
  maxZoom: '1.0000000000',
  snapEnabled: 'false',
  snapSize: '1000.0000000000',
  snapMode: 'center',
  defaultElevation: '15.0000000000',
};

export interface StarjumpFleetExport {
  type: string;
  version: number;
  canvasItems: Record<string, unknown>[];
  [key: string]: unknown;
}

export const buildFleetViewerExport = (ships: Ship[]): FleetViewerExport =>
  ships.map((ship) => ({
    name: ship.name,
    shipname: ship.nick?.trim() || '',
    type: 'ship',
  }));

export const buildStarjumpFleetExport = (ships: Ship[]): StarjumpFleetExport => {
  const unique = uniqueShipsByName(ships);
  const canvasItems: Record<string, unknown>[] = [];

  unique.forEach((ship) => {
    const shipId = createId();
    const textGroupId = createId();
    const slug = toStarjumpShipSlug(ship);
    const label = displayShipLabel(ship);
    const visuals = getStarjumpShipVisuals(ship);

    canvasItems.push({
      id: shipId,
      itemType: 'SHIP',
      x: '0.0000000000',
      y: '0.0000000000',
      topRotation: '0.0000000000',
      rotation: '0.0000000000',
      zoom: '1.0000000000',
      viewAngle: 'iso',
      imageRes: visuals.imageRes,
      imageScale: visuals.imageScale,
      dropShadow: 'true',
      elevation: '15.0000000000',
      shipSlug: slug,
      variantSlug: '',
      width: visuals.width,
      height: visuals.height,
      autoPositionDistance: '800.0000000000',
      autoPositionAlignment: "Instance of 'minified:Class2461'",
      defaultText: label,
      locked: 'false',
      selectionGroup: '',
      parentOfGroup: textGroupId,
    });

    canvasItems.push({
      id: textGroupId,
      itemType: 'TEXTGROUP',
      x: '0.0000000000',
      y: visuals.textGroupY,
      text: label,
      width: visuals.textWidth,
      childOfShip: shipId,
      ...STARJUMP_TEXTGROUP_DEFAULTS,
    });
  });

  return {
    ...STARJUMP_DOCUMENT_DEFAULTS,
    canvasItems,
  };
};

export interface FleetExportFilenameParts {
  manufacturer?: string;
  type?: string;
  size?: string;
  user?: string;
}

export const buildExportFilename = (
  prefix: string,
  parts: FleetExportFilenameParts = {}
): string => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
  const manufacturerSuffix =
    parts.manufacturer && parts.manufacturer !== 'all' ? `_${parts.manufacturer}` : '';
  const typeSuffix = parts.type ? `_${parts.type}` : '';
  const sizeSuffix = parts.size ? `_${parts.size}` : '';
  const userSuffix = parts.user ? `_${parts.user}` : '';
  return `${prefix}${manufacturerSuffix}${typeSuffix}${sizeSuffix}${userSuffix}_${timestamp}.json`;
};

export const downloadJsonFile = (data: unknown, filename: string): void => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
